import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, api } from '@/lib/api';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { exponentOf } from '@/lib/currencies';
import { ltr } from '@/lib/arabic';
import { useT } from '@/lib/i18n';
import { formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { debitNoteApplyProblems, useOpenBills, type PurchaseDocument } from '@/lib/purchases';

/** E6 "Apply to bills": settle the vendor's open bills from a debit note (original bill first). */
export function ApplyDebitNoteDialog({
  note,
  open,
  onClose,
}: {
  note: PurchaseDocument;
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const bills = useOpenBills(open ? note.contactId : null);
  const [date, setDate] = useState(todayIso());
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  // amounts are in the document's currency (multi-currency, migration 0900)
  const ccy = note.currency;
  const exp = exponentOf(ccy);
  const available = toMinor(note.balance, exp) ?? 0n;
  // a debit note applies to bills only (journal entry charges on A/P are paid in Pay bills)
  const billRows = useMemo(
    () => (bills.data?.bills ?? []).filter((b) => b.kind === 'bill'),
    [bills.data],
  );

  useEffect(() => {
    if (!open || !bills.data) return;
    setDate(todayIso() < note.issueDate ? note.issueDate : todayIso());
    const next: Record<string, string> = {};
    let left = available;
    const list = [...billRows].sort((a, b) =>
      a.id === note.relatedDocumentId ? -1 : b.id === note.relatedDocumentId ? 1 : 0,
    );
    for (const b of list) {
      if (left <= 0n) break;
      const bal = toMinor(b.balance, exp) ?? 0n;
      const take = bal < left ? bal : left;
      next[b.id] = minorToPlain(take, exp);
      left -= take;
    }
    setAmounts(next);
  }, [open, bills.data, billRows, available, note.issueDate, note.relatedDocumentId]);

  const checks = debitNoteApplyProblems(amounts, billRows, available, exp);
  const total = checks.total;
  const m = useMutation({
    mutationFn: () =>
      api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${note.id}/apply`, {
        method: 'POST',
        body: JSON.stringify({
          date,
          allocations: Object.entries(amounts)
            .filter(([, v]) => (toMinor(v, exp) ?? 0n) > 0n)
            .map(([documentId, amount]) => ({ documentId, amount })),
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      toast(
        t('purchasePages.applyDebitNote.applied', {
          number: ltr(note.number ?? ''),
          amount: ltr(`${ccy} ${formatMinor(total, exp)}`),
        }),
      );
      onClose();
    },
  });
  // a failed attempt's error must not show the next time the dialog opens
  const close = () => {
    m.reset();
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t('purchasePages.applyDebitNote.title', { number: ltr(note.number ?? '') })}
          </DialogTitle>
          <DialogDescription>
            {t('purchasePages.applyDebitNote.description', {
              amount: ltr(`${ccy} ${formatAmount(note.balance)}`),
              vendor: `\u2068${note.contactName ?? ''}\u2069`,
            })}
          </DialogDescription>
        </DialogHeader>
        {m.error ? (
          <ErrorBanner>
            {m.error instanceof ApiError ? m.error.message : String(m.error)}
          </ErrorBanner>
        ) : null}
        <Field label={t('purchasePages.applyDebitNote.dateApplied')} className="w-[200px]">
          {(id) => <DateInput id={id} value={date} onChange={setDate} />}
        </Field>
        {bills.isPending ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : billRows.length ? (
          <table
            className="w-full text-sm"
            aria-label={t('purchasePages.applyDebitNote.openBills')}
          >
            <thead className="text-start text-xs uppercase text-muted-foreground">
              <tr className="border-b">
                <th className="py-2">{t('purchasePages.applyDebitNote.bill')}</th>
                <th className="py-2">{t('purchasePages.applyDebitNote.dueDate')}</th>
                <th className="py-2 text-end">{t('purchasePages.applyDebitNote.openBalance')}</th>
                <th className="w-[160px] py-2 text-end">
                  {t('purchasePages.applyDebitNote.apply')}
                </th>
              </tr>
            </thead>
            <tbody>
              {billRows.map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-1.5">
                    {b.number}
                    {b.reference ? (
                      <span className="text-muted-foreground"> · {b.reference}</span>
                    ) : null}
                  </td>
                  <td className="py-1.5">{b.dueDate ? formatDate(b.dueDate) : ''}</td>
                  <td className="tnum py-1.5 text-end">{formatAmount(b.balance)}</td>
                  <td className="py-1">
                    <AmountInput
                      exp={exp}
                      aria-label={t('purchasePages.applyDebitNote.applyTo', {
                        number: ltr(b.number),
                      })}
                      value={amounts[b.id] ?? ''}
                      invalid={checks.over.has(b.id)}
                      onValueChange={(v) => setAmounts((a) => ({ ...a, [b.id]: v }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('purchasePages.applyDebitNote.noOpenBills')}
          </p>
        )}
        <p
          className={`text-sm ${checks.problems.length ? 'text-destructive' : 'text-muted-foreground'}`}
        >
          {t('purchasePages.applyDebitNote.applying', {
            total: ltr(`${ccy} ${formatMinor(total, exp)}`),
            available: ltr(`${ccy} ${formatMinor(available, exp)}`),
          })}
        </p>
        {checks.problems.length ? (
          <ul className="list-disc ps-4 text-sm text-destructive" role="alert">
            {checks.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={m.isPending || total === 0n || checks.problems.length > 0}
            onClick={() => m.mutate()}
          >
            {m.isPending
              ? t('purchasePages.applyDebitNote.applyingButton')
              : t('purchasePages.applyDebitNote.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
